// The tensor app's state and the pure functions that change it (no I/O):
// what bootstrap gives, the workbooks that are open on this device (their
// items, budgets and rows), and changes of rows waiting to be sent.
//
// Rows, items and budgets are kept unless an equal or newer version is there
// already; a deleted row stays, marked, so that an older copy (a page read
// before the change) cannot bring it back. Rows are named by client_id on
// this device: a row added here has none of the server's id until it is sent.
import type {
  Id,
  RowKind,
  TensorBootstrap,
  TensorBudget,
  TensorChange,
  TensorDef,
  TensorDim,
  TensorEntry,
  TensorItem,
  TensorPerson,
  TensorSavedView,
  TensorRow,
  TensorRowCreate,
  TensorWorkbook,
  TensorWorkbookFull,
} from './api'

export interface Book {
  items: Record<number, TensorItem>
  /** by budgetKey(month, category); amount 0 is no budget */
  budgets: Record<string, TensorBudget>
  /** by id, deleted ones too */
  rows: Record<number, TensorRow>
  /** rows are still being read (pages) */
  loading: boolean
  /** dimensions people made (design 02 3.2), by id; deleted ones stay, marked */
  dims: Record<number, TensorDim>
  /** input tensors (design 02 3.1), by id; deleted ones stay, marked */
  tensors: Record<number, TensorDef>
  /** their cells: tensor id -> coord -> cell (value null: cleared) */
  entries: Record<number, Record<string, TensorEntry>>
  /** saved views (design 02 3.6), by id; deleted ones stay, marked */
  views: Record<number, TensorSavedView>
}

/** The fields of a row a change sets (items by id; null is none). */
export interface RowFields {
  kind: RowKind
  day: string
  amount: number
  person_item: Id | null
  category_item: Id | null
  payment_item: Id | null
  memo: string
}

export type RowPatch = Partial<RowFields>

export type OpStatus = 'sending' | 'waiting'

/** A change of a row, sent in order (design 02 4.4). */
export type RowOp =
  | { key: string; type: 'add'; workbookId: Id; clientId: string; fields: RowFields; status: OpStatus }
  | { key: string; type: 'edit'; workbookId: Id; clientId: string; patch: RowPatch; version: number; status: OpStatus }
  | { key: string; type: 'delete'; workbookId: Id; clientId: string; status: OpStatus }

export interface Me {
  id: Id
  name: string
  admin: boolean
}

export interface TState {
  phase: 'loading' | 'signed-out' | 'ready'
  signedOutReason: string | null
  connection: 'online' | 'connecting' | 'offline'
  me: Me | null
  users: TensorPerson[]
  cursor: number
  workbooks: Record<number, TensorWorkbook>
  /** what is read of a workbook (the open one, and those opened before) */
  books: Record<number, Book>
  ops: RowOp[]
  /** the workbook on screen (0: none yet) */
  current: Id
}

export const initialState: TState = {
  phase: 'loading',
  signedOutReason: null,
  connection: 'connecting',
  me: null,
  users: [],
  cursor: 0,
  workbooks: {},
  books: {},
  ops: [],
  current: 0,
}

export function fromBootstrap(s: TState, b: TensorBootstrap): TState {
  const workbooks: Record<number, TensorWorkbook> = {}
  for (const w of b.workbooks) workbooks[w.id] = w
  return {
    ...s,
    phase: 'ready',
    signedOutReason: null,
    me: { id: b.me.id, name: b.me.name ?? '', admin: b.me.admin === true },
    users: b.users,
    cursor: b.cursor,
    workbooks,
    // what was read before may have missed changes: read again when opened
    books: {},
    ops: s.ops.filter((o) => workbooks[o.workbookId]),
    current: workbooks[s.current] ? s.current : 0,
  }
}

export function budgetKey(month: string, categoryItem: Id): string {
  return `${month}|${categoryItem}`
}

/** A workbook just read (GET /workbooks/{id}); its rows come by pages. */
export function bookFromFull(full: TensorWorkbookFull, old?: Book): Book {
  let book: Book = {
    items: old?.items ?? {},
    budgets: old?.budgets ?? {},
    rows: old?.rows ?? {},
    loading: true,
    dims: old?.dims ?? {},
    tensors: old?.tensors ?? {},
    entries: old?.entries ?? {},
    views: old?.views ?? {},
  }
  for (const it of full.items) book = putItem(book, it)
  for (const b of full.budgets) book = putBudget(book, b)
  for (const d of full.dims ?? []) book = putDim(book, d)
  for (const t of full.tensors ?? []) book = putTensor(book, t)
  for (const v of full.views ?? []) book = putView(book, v)
  return book
}

export function putView(book: Book, v: TensorSavedView): Book {
  const old = book.views[v.id]
  if (old && old.version >= v.version) return book
  return { ...book, views: { ...book.views, [v.id]: v } }
}

/** An empty book (being read). */
export function emptyBook(): Book {
  return { items: {}, budgets: {}, rows: {}, loading: true, dims: {}, tensors: {}, entries: {}, views: {} }
}

export function putTensor(book: Book, t: TensorDef): Book {
  const old = book.tensors[t.id]
  if (old && old.version >= t.version) return book
  const next = { ...book, tensors: { ...book.tensors, [t.id]: t } }
  if (t.deleted && next.entries[t.id]) {
    const entries = { ...next.entries }
    delete entries[t.id]
    next.entries = entries
  }
  return next
}

/** A tensor's cells as the server says they are now: the later one wins
    (updated_at, then version), so that an old page read meanwhile does not
    bring a cleared cell back. */
export function putEntries(book: Book, tensorId: Id, cells: readonly TensorEntry[]): Book {
  if (book.tensors[tensorId]?.deleted) return book
  let next: Record<string, TensorEntry> | null = null
  for (const c of cells) {
    const cur = (next ?? book.entries[tensorId] ?? {})[c.coord]
    if (cur && (cur.updated_at > c.updated_at || (cur.updated_at === c.updated_at && cur.version > c.version))) continue
    if (!next) next = { ...(book.entries[tensorId] ?? {}) }
    next[c.coord] = c
  }
  return next ? { ...book, entries: { ...book.entries, [tensorId]: next } } : book
}

export function putDim(book: Book, d: TensorDim): Book {
  const old = book.dims[d.id]
  if (old && old.version >= d.version) return book
  return { ...book, dims: { ...book.dims, [d.id]: d } }
}

export function putItem(book: Book, item: TensorItem): Book {
  const old = book.items[item.id]
  if (old && old.version >= item.version) return book
  return { ...book, items: { ...book.items, [item.id]: item } }
}

export function putBudget(book: Book, b: TensorBudget): Book {
  const k = budgetKey(b.month, b.category_item)
  const old = book.budgets[k]
  if (old && (old.version > b.version || (old.version === b.version && old.updated_at >= b.updated_at))) return book
  return { ...book, budgets: { ...book.budgets, [k]: b } }
}

export function putRows(book: Book, rows: TensorRow[]): Book {
  let next: Record<number, TensorRow> | null = null
  for (const r of rows) {
    const cur = (next ?? book.rows)[r.id]
    if (cur && cur.version >= r.version) continue
    if (!next) next = { ...book.rows }
    next[r.id] = r
  }
  return next ? { ...book, rows: next } : book
}

export function putWorkbook(s: TState, w: TensorWorkbook): TState {
  const old = s.workbooks[w.id]
  if (old && old.version > w.version) return s
  if (old && old.version === w.version && old.updated_at >= w.updated_at) return s
  return { ...s, workbooks: { ...s.workbooks, [w.id]: w } }
}

/** The workbook is gone or no longer mine to see: drop all of it. */
export function removeWorkbook(s: TState, id: Id): TState {
  if (!s.workbooks[id] && !s.books[id] && !s.ops.some((o) => o.workbookId === id)) return s
  const workbooks = { ...s.workbooks }
  delete workbooks[id]
  const books = { ...s.books }
  delete books[id]
  return {
    ...s,
    workbooks,
    books,
    ops: s.ops.filter((o) => o.workbookId !== id),
    current: s.current === id ? 0 : s.current,
  }
}

function updateBook(s: TState, id: Id, fn: (b: Book) => Book): TState {
  const book = s.books[id]
  if (!book) return s // not read here: read whole when opened
  const next = fn(book)
  return next === book ? s : { ...s, books: { ...s.books, [id]: next } }
}

export function applyChange(s: TState, c: TensorChange): TState {
  switch (c.type) {
    case 'workbook.updated':
      return putWorkbook(s, c.data as TensorWorkbook)
    case 'workbook.removed':
      return removeWorkbook(s, c.workbook_id)
    case 'item.updated':
      return updateBook(s, c.workbook_id, (b) => putItem(b, c.data as TensorItem))
    case 'row.updated':
      return updateBook(s, c.workbook_id, (b) => putRows(b, [c.data as TensorRow]))
    case 'budget.updated':
      return updateBook(s, c.workbook_id, (b) => putBudget(b, c.data as TensorBudget))
    case 'dim.updated':
      return updateBook(s, c.workbook_id, (b) => putDim(b, c.data as TensorDim))
    case 'tensor.updated':
      return updateBook(s, c.workbook_id, (b) => putTensor(b, c.data as TensorDef))
    case 'view.updated':
      return updateBook(s, c.workbook_id, (b) => putView(b, c.data as TensorSavedView))
    case 'tensor.cells': {
      const d = c.data as { tensor_id: Id; cells: TensorEntry[] }
      return updateBook(s, c.workbook_id, (b) => putEntries(b, d.tensor_id, d.cells))
    }
    default:
      return s
  }
}

export function withBook(s: TState, id: Id, fn: (b: Book) => Book): TState {
  return updateBook(s, id, fn)
}

// ---- rows as the screen shows them ----

/** A row with this device's waiting changes applied. */
export interface ShownRow extends RowFields {
  /** 0 until the server has it */
  id: Id
  client_id: string
  version: number
  created_by: Id
  created_at: number
  /** changes of it are still to be sent */
  pending?: OpStatus
}

function fieldsOf(r: TensorRow): RowFields {
  return {
    kind: r.kind,
    day: r.day,
    amount: r.amount,
    person_item: r.person_item,
    category_item: r.category_item,
    payment_item: r.payment_item,
    memo: r.memo,
  }
}

/** The rows of a workbook (not deleted), with the waiting changes on top. */
export function shownRows(book: Book | undefined, ops: RowOp[], workbookId: Id, me: Id, now = Date.now()): ShownRow[] {
  const mine = ops.filter((o) => o.workbookId === workbookId)
  const byClient = new Map<string, RowOp[]>()
  for (const o of mine) {
    const l = byClient.get(o.clientId)
    if (l) l.push(o)
    else byClient.set(o.clientId, [o])
  }
  const out: ShownRow[] = []
  const seen = new Set<string>()
  const take = (base: ShownRow | null, clientId: string) => {
    let row = base
    let pending: OpStatus | undefined
    for (const o of byClient.get(clientId) ?? []) {
      pending = pending === 'waiting' || o.status === 'waiting' ? 'waiting' : 'sending'
      if (o.type === 'add') {
        if (!row)
          row = { ...o.fields, id: 0, client_id: clientId, version: 0, created_by: me, created_at: now }
      } else if (o.type === 'edit') {
        if (row) row = { ...row, ...o.patch }
      } else row = null
    }
    if (!row) return
    out.push(pending ? { ...row, pending } : row)
  }
  for (const r of Object.values(book?.rows ?? {})) {
    seen.add(r.client_id)
    if (r.deleted) continue
    take(
      {
        ...fieldsOf(r),
        id: r.id,
        client_id: r.client_id,
        version: r.version,
        created_by: r.created_by,
        created_at: r.created_at,
      },
      r.client_id,
    )
  }
  for (const id of byClient.keys()) if (!seen.has(id)) take(null, id)
  return out
}

/** Newest day first; on a day, the one written last first. */
export function sortRows(rows: ShownRow[]): ShownRow[] {
  return [...rows].sort((a, b) =>
    a.day !== b.day ? (a.day < b.day ? 1 : -1) : b.created_at - a.created_at || b.id - a.id,
  )
}

/** A server row by client_id. */
export function rowByClient(book: Book | undefined, clientId: string): TensorRow | undefined {
  if (!book) return undefined
  for (const r of Object.values(book.rows)) if (r.client_id === clientId) return r
  return undefined
}

/** The body that creates a row (items by id; 0 is none). */
export function createBody(clientId: string, f: RowFields): TensorRowCreate {
  return {
    client_id: clientId,
    kind: f.kind,
    day: f.day,
    amount: f.amount,
    person_item: f.person_item ?? 0,
    category_item: f.category_item ?? 0,
    payment_item: f.payment_item ?? 0,
    memo: f.memo,
  }
}

/** A patch as the API takes it (0 for no item). */
export function patchBody(p: RowPatch): Record<string, string | number> {
  const out: Record<string, string | number> = {}
  for (const [k, v] of Object.entries(p)) {
    if (v === undefined) continue
    out[k] = v === null ? 0 : v
  }
  return out
}

/** Adds an op to the queue; an edit or delete of a row that is not sent
    yet changes its add instead (nothing goes for a row the server never had). */
export function queueOp(ops: RowOp[], op: RowOp): RowOp[] {
  if (op.type === 'add') return [...ops, op]
  const i = ops.findIndex((o) => o.type === 'add' && o.clientId === op.clientId && o.status === 'waiting')
  // an add being sent may already be on the server: then the op follows it
  if (i >= 0 && !ops.some((o, j) => j > i && o.clientId === op.clientId)) {
    const add = ops[i]!
    if (add.type !== 'add') return [...ops, op]
    if (op.type === 'delete') return ops.filter((_, j) => j !== i)
    const next = [...ops]
    next[i] = { ...add, fields: { ...add.fields, ...op.patch } }
    return next
  }
  return [...ops, op]
}

/** Whose name is this user id (the family list from bootstrap). */
export function userName(s: Pick<TState, 'users'>, id: Id): string {
  return s.users.find((u) => u.id === id)?.name ?? '알 수 없음'
}
